import { useState, useMemo, useEffect } from "react";
import { KeycloakTokenParsed } from "keycloak-js";
import { toast } from 'react-toastify';

import { createProjectDailyUpdates, updateProjectDailyUpdates, getProjectDailyUpdatesById, deleteProjectDailyUpdates } from "../../api/dailyUpdates";


import { AddDailyUpdateModal } from './AddDailyUpdateModal';
import { DailyUpdatesDocumentAttachment } from './DailyUpdatesDocumentAttachment';

import { 
  FaPlus,
  FaSearch,
  FaFilter,
  FaChevronDown,
  FaCircle,
  FaCalendarAlt,
  FaTrash
} from 'react-icons/fa';

import { 
  getTableHeaderStyle, 
  getFormLabelStyle, 
  getMultilineFieldStyle,
  getFormSelectContainerStyle,
  getFormSelectStyle,
  DesignSystem,
} from '../../styles/designSystem';

import { useKeycloak } from '@react-keycloak/web';

interface TabDailyUpdatesProps {
  projectId: string | undefined;
  token: string | undefined;
  projectData: ProjectInterface,
  tokenParsed: KeycloakTokenParsed | undefined;
  onProjectUpdate?: () => Promise<void>;
  dailyUpdates: ProjectDailyUpdate[];
  highlightedItemId?: string | null;
}

// Daily Update row component in ProjectPlan2 style
const DailyUpdateRow = ({ 
  dailyUpdate, 
  projectData,
  onSubmit,
  onDelete,
  onProjectUpdate: _onProjectUpdate,
  isNew = false,
  isHighlighted = false
}: { 
  dailyUpdate: ProjectDailyUpdate, 
  projectData: ProjectInterface,
  onSubmit: Function,
  onDelete: (dailyUpdateId: string) => void,
  onProjectUpdate?: () => Promise<void>,
  isNew?: boolean,
  isHighlighted?: boolean
}) => {
  const [form, setForm] = useState({ ...dailyUpdate });
  const [isExpanded, setIsExpanded] = useState(false); // Collapsed by default
  const [isSaving, setIsSaving] = useState(false);
  const [_originalData, setOriginalData] = useState({ ...dailyUpdate });

  // Function to count number of lines in text
  const calculateRows = (text: string, maxRows: number = 5) => {
    if (!text) return 1;
    const lines = text.split('\n').length;
    const estimatedWrappedLines = Math.ceil(text.length / 50); // Approximately 50 characters per line
    return Math.min(Math.max(lines, Math.ceil(estimatedWrappedLines)), maxRows);
  };

  const { keycloak: { token: _token } } = useKeycloak();

  useEffect(() => {
    setForm({ ...dailyUpdate });
    setOriginalData({ ...dailyUpdate });
  }, [dailyUpdate]);

  // Function to save changes
  const saveChanges = async (updatedForm: any) => {
    setIsSaving(true);
    try {
      await onSubmit(updatedForm);
      setOriginalData({ ...updatedForm });
    } finally {
      setIsSaving(false);
    }
  };

  const handleFieldChange = (field: string, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
    
    // Auto-save for certain fields immediately
    if (['date', 'location', 'resources_onsite'].includes(field)) {
      const updatedForm = { ...form, [field]: value };
      saveChanges(updatedForm);
    }
  };

  // Function to handle text field blur events
  const handleFieldBlur = (field: string, value: any) => {
    const updatedForm = { 
      ...form, 
      [field]: value 
    };
    
    setForm(updatedForm);
    saveChanges(updatedForm);
  };

  // Function to refresh daily update data after document operations
  const handleDailyUpdateUpdate = async () => {
    if (!_token || !projectData?.id) return;
    
    try {
      const updatedDailyUpdate = await getProjectDailyUpdatesById(_token, projectData.id, dailyUpdate.id);
      setForm(updatedDailyUpdate);
      
      // Also trigger the parent update to refresh the list
      if (_onProjectUpdate) {
        await _onProjectUpdate();
      }
    } catch (error) {
      console.error('Failed to refresh daily update data:', error);
    }
  };

  return (
    <div className={`group hover:bg-gray-50/50 transition-all duration-200 ${
      isNew ? 'bg-blue-50/30' : ''
    } ${
      isHighlighted ? 'bg-yellow-50/50 border-l-4 border-yellow-400' : ''
    }`}>
      {/* Main Row */}
      <div className="flex items-start px-6 py-4 relative" style={{ borderBottom: '0.5px solid #E5E7EB' }}>
        {/* Clickable Area for Expand */}
        <div 
          className="absolute inset-0 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        />
        
        {/* Expand Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          className="mr-4 p-1 rounded-md hover:bg-gray-100 transition-colors relative z-10 flex items-center"
        >
          <FaChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </button>

        {/* Status Indicator */}
        <div className="mr-4 relative z-10 flex items-center space-x-2">
          <div className="relative">
            {/* Rotating Ring around status dot - when saving any field */}
            {isSaving && (
              <div className="absolute inset-0 w-5 h-5 -ml-1.5 -mt-1.5 rounded-full border-2 border-transparent animate-spin border-t-indigo-600/60 border-r-indigo-600/60" style={{ animation: 'spin 3s linear infinite' }} />
            )}
            {/* Status Dot */}
            <FaCircle 
              className="w-2 h-2 text-indigo-600"
            />
          </div>
        </div>

        {/* Date */}
        <div className="mx-3 w-24 flex items-center text-xs text-gray-600 relative z-10">
          <input
            type="date"
            value={form.date ? new Date(form.date).toISOString().split('T')[0] : ''}
            onChange={(e) => {
              const value = e.target.value ? new Date(e.target.value).toISOString() : '';
              handleFieldChange('date', value);
            }}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            className="bg-transparent border-0 focus:outline-none text-xs w-full"
          />
        </div>

        {/* Location */}
        <div className="mx-3 w-24 relative z-10 flex items-start">
          <textarea
            value={form.location || ''}
            onChange={(e) => setForm(prev => ({ ...prev, location: e.target.value }))}
            onBlur={(e) => handleFieldBlur('location', e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            className="w-full bg-transparent border-0 focus:outline-none text-left resize-none"
            style={{ 
              fontSize: '13px',
              fontFamily: DesignSystem.typography.body.fontFamily,
              color: '#4B5563',
              lineHeight: '1.3'
            }}
            placeholder="Location"
            rows={3}
          />
        </div>

        {/* Resources Onsite */}
        <div className="mx-3 w-16 relative z-10 flex items-center">
          <input
            type="number"
            min="0"
            value={form.resources_onsite || ''}
            onChange={(e) => {
              const value = e.target.value ? Number(e.target.value) : 0;
              handleFieldChange('resources_onsite', value);
            }}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            className="w-full bg-transparent border-0 focus:outline-none text-sm text-center"
            placeholder="0"
          />
        </div>

        {/* What Done (Summary) */}
        <div className="flex-[2] min-w-0 mx-3 relative z-10 flex items-start">
          <textarea
            value={form.what_done || ''}
            onChange={(e) => setForm(prev => ({ ...prev, what_done: e.target.value }))}
            onBlur={(e) => handleFieldBlur('what_done', e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            className="flex-1 bg-transparent border-0 focus:outline-none placeholder-gray-400 resize-none"
            style={getMultilineFieldStyle()}
            placeholder="What was accomplished today..."
            rows={calculateRows(form.what_done || '', 5)}
          />
        </div>

        {/* What is the goal for next day? */}
        <div className="flex-[1.5] min-w-0 mx-3 relative z-10 flex items-start">
          <textarea
            value={form.what_goal || ''}
            onChange={(e) => setForm(prev => ({ ...prev, what_goal: e.target.value }))}
            onBlur={(e) => handleFieldBlur('what_goal', e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            className="flex-1 bg-transparent border-0 focus:outline-none placeholder-gray-400 resize-none"
            style={{ 
              fontSize: '12px',
              fontFamily: DesignSystem.typography.body.fontFamily,
              color: '#4B5563',
              lineHeight: '1.3'
            }}
            placeholder="What are the goals for next day..."
            rows={calculateRows(form.what_goal || '', 5)}
          />
        </div>

        {/* What are the road blocks? */}
        <div className="flex-[1.5] min-w-0 mx-3 relative z-10 flex items-start">
          <textarea
            value={form.what_blocks || ''}
            onChange={(e) => setForm(prev => ({ ...prev, what_blocks: e.target.value }))}
            onBlur={(e) => handleFieldBlur('what_blocks', e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            className="flex-1 bg-transparent border-0 focus:outline-none placeholder-gray-400 resize-none"
            style={{ 
              fontSize: '12px',
              fontFamily: DesignSystem.typography.body.fontFamily,
              color: '#6B7280',
              lineHeight: '1.3'
            }}
            placeholder="Any blockers or issues..."
            rows={calculateRows(form.what_blocks || '', 5)}
          />
        </div>

        {/* Documents */}
        <div className="ml-3 w-32 relative z-10">
          <div onClick={(e) => e.stopPropagation()}>
            <DailyUpdatesDocumentAttachment 
              projectId={projectData?.id || ''} 
              recordId={dailyUpdate.id}
              documentUrl={form.document || undefined}
              fileName={form.file_name || undefined}
              onUpdate={handleDailyUpdateUpdate}
            />
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="px-6 pb-6 pt-2" style={{ 
          backgroundColor: DesignSystem.colors.background.gray50,
          borderTop: `0.5px solid ${DesignSystem.colors.border.gray200}` 
        }}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-4">
              {/* What was Done? - Full */}
              <div className="pt-2">
                <label className="block mb-2" style={getFormLabelStyle()}>What was Done?</label>
                <textarea
                  value={form.what_done || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, what_done: e.target.value }))}
                  onBlur={(e) => handleFieldBlur('what_done', e.target.value)}
                  className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  placeholder="Describe what was accomplished today..."
                  rows={5}
                  style={getMultilineFieldStyle()}
                />
              </div>
              
              {/* What is the goal for next day? */}
              <div>
                <label className="block mb-2" style={getFormLabelStyle()}>What is the goal for next day?</label>
                <textarea
                  value={form.what_goal || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, what_goal: e.target.value }))}
                  onBlur={(e) => handleFieldBlur('what_goal', e.target.value)}
                  className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  placeholder="What are the goals for next day..."
                  rows={5}
                  style={{ 
                    fontFamily: DesignSystem.typography.body.fontFamily,
                    fontSize: '12px',
                    color: '#4B5563',
                    lineHeight: '1.3'
                  }}
                />
              </div>

              {/* What are the road blocks? */}
              <div>
                <label className="block mb-2" style={getFormLabelStyle()}>What are the road blocks?</label>
                <textarea
                  value={form.what_blocks || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, what_blocks: e.target.value }))}
                  onBlur={(e) => handleFieldBlur('what_blocks', e.target.value)}
                  className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  placeholder="Any blockers or issues..."
                  rows={5}
                  style={{ 
                    fontFamily: DesignSystem.typography.body.fontFamily,
                    fontSize: '12px',
                    color: '#6B7280',
                    lineHeight: '1.3'
                  }}
                />
              </div>
            </div>

            {/* Daily Update Details */}
            <div className="space-y-4">
              {/* Date */}
              <div className="pt-2">
                <label className="block mb-2" style={getFormLabelStyle()}>Date</label>
                <input
                  type="date"
                  value={form.date ? new Date(form.date).toISOString().split('T')[0] : ''}
                  onChange={(e) => {
                    const value = e.target.value ? new Date(e.target.value).toISOString() : '';
                    handleFieldChange('date', value);
                  }}
                  className="w-full text-sm text-gray-700 bg-white border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Location */}
              <div>
                <label className="block mb-2" style={getFormLabelStyle()}>Location</label>
                <input
                  type="text"
                  value={form.location || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, location: e.target.value }))}
                  onBlur={(e) => handleFieldBlur('location', e.target.value)}
                  className="w-full text-sm text-gray-700 bg-white border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter location..."
                />
              </div>

              {/* # of Resource Onsite */}
              <div>
                <label className="block mb-2" style={getFormLabelStyle()}># of Resource Onsite</label>
                <input
                  type="number"
                  min="0"
                  value={form.resources_onsite || ''}
                  onChange={(e) => {
                    const value = e.target.value ? Number(e.target.value) : 0;
                    handleFieldChange('resources_onsite', value);
                  }}
                  className="w-full text-sm text-gray-700 bg-white border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0"
                />
              </div>

              {/* Actions */}
              <div className="pt-2">
                <button
                  onClick={() => onDelete(dailyUpdate.id!)}
                  className="flex items-center space-x-2 px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <FaTrash className="w-3 h-3" />
                  <span>Delete Update</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const TabDailyUpdates = ({
  token,
  projectData,
  projectId,
  onProjectUpdate,
  dailyUpdates,
  highlightedItemId,
}: TabDailyUpdatesProps) => {
  // Add cleanup flag to prevent DOM operations after unmount
  const [isUnmounted, setIsUnmounted] = useState(false);
  // Filters and search
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [dateFilter, setDateFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [showAddDailyUpdate, setShowAddDailyUpdate] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Scroll detection
  useEffect(() => {
    if (isUnmounted) return;

    const handleScroll = () => {
      if (!isUnmounted) {
      setIsScrolled(window.scrollY > 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isUnmounted]);

  // Cleanup effect to prevent DOM operations after unmount
  useEffect(() => {
    return () => {
      setIsUnmounted(true);
    };
  }, []);

  const updateDailyUpdateHandler = async (form: ProjectDailyUpdate) => {
    try {
      if (token && projectId) {
        await updateProjectDailyUpdates(token, projectId, form.id, form);
        toast.success('Daily update updated successfully');
        
        if (onProjectUpdate) {
          await onProjectUpdate();
        }
      }
    } catch (error: any) {
      console.error('Error updating daily update:', error);
      toast.error(error.message || 'Failed to update daily update');
    }
  };

  const handleAddDailyUpdate = async (dailyUpdateData: any) => {
    try {
      if (token && projectId) {
        await createProjectDailyUpdates(token, projectId, dailyUpdateData);
        toast.success('Daily update added successfully');
        setShowAddDailyUpdate(false);
        
        if (onProjectUpdate) {
          await onProjectUpdate();
        }
      }
    } catch (error: any) {
      console.error('Error adding daily update:', error);
      toast.error(error.message || 'Failed to add daily update');
    }
  };

  const deleteDailyUpdateHandler = async (dailyUpdateId: string) => {
    if (!token || !projectId) return;
    
    if (!window.confirm('Are you sure you want to delete this daily update?')) return;
    
    try {
      await deleteProjectDailyUpdates(token, projectId, dailyUpdateId);
      toast.success('Daily update deleted successfully');
      
      if (onProjectUpdate) {
        await onProjectUpdate();
      }
    } catch (error: any) {
      console.error('Error deleting daily update:', error);
      toast.error(error.message || 'Failed to delete daily update');
    }
  };

  const getDateCount = (period: string) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const thisWeek = new Date(today);
    thisWeek.setDate(thisWeek.getDate() - 7);

    switch (period) {
      case 'today':
        return dailyUpdates.filter(item => {
          const itemDate = new Date(item.date);
          return itemDate.toDateString() === today.toDateString();
        }).length;
      case 'yesterday':
        return dailyUpdates.filter(item => {
          const itemDate = new Date(item.date);
          return itemDate.toDateString() === yesterday.toDateString();
        }).length;
      case 'this_week':
        return dailyUpdates.filter(item => {
          const itemDate = new Date(item.date);
          return itemDate >= thisWeek;
        }).length;
      default:
        return 0;
    }
  };

  // Filter and search logic
  const filteredDailyUpdates = useMemo(() => {
    let filtered = dailyUpdates;

    if (searchQuery) {
      filtered = filtered.filter(item =>
        item.what_done?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.what_goal?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.what_blocks?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.location?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (dateFilter !== 'all') {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const thisWeek = new Date(today);
      thisWeek.setDate(thisWeek.getDate() - 7);

      filtered = filtered.filter(item => {
        const itemDate = new Date(item.date);
        switch (dateFilter) {
          case 'today':
            return itemDate.toDateString() === today.toDateString();
          case 'yesterday':
            return itemDate.toDateString() === yesterday.toDateString();
          case 'this_week':
            return itemDate >= thisWeek;
          default:
            return true;
        }
      });
    }

    if (locationFilter !== 'all') {
      filtered = filtered.filter(item => item.location === locationFilter);
    }

    return filtered;
  }, [dailyUpdates, searchQuery, dateFilter, locationFilter]);

  // Get unique locations for filter
  const uniqueLocations = useMemo(() => {
    const locations = dailyUpdates.map(item => item.location).filter(Boolean);
    return [...new Set(locations)];
  }, [dailyUpdates]);

  return (
    <div className="min-h-screen bg-white">
      {/* Header with Search, Actions and Table Header - Fixed Sticky Section */}
      <div 
        className="sticky top-0"
        style={{ zIndex: 100 }}
      >
        <div className="max-w-[1280px] mx-auto px-0 py-4">
          <div className={`bg-white rounded-lg transition-shadow duration-200 ${isScrolled ? 'shadow-md' : ''}`} style={{ border: `0.5px solid ${DesignSystem.colors.border.gray200}` }}>
            {/* Search and Actions Row */}
            <div className="px-6 py-4" style={{ borderBottom: '0.5px solid #E5E7EB' }}>
              <div className="flex items-center justify-between">
                {/* Left side - Search and Quick Stats */}
                <div className="flex items-center space-x-6">
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search daily updates..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-4 py-2.5 w-80 text-sm bg-gray-50 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                    />
                  </div>
                  
                  {/* Quick Stats */}
                  <div className="flex items-center space-x-4 text-sm">
                    <span className="text-gray-500">
                      {filteredDailyUpdates.length} of {dailyUpdates.length} updates
                    </span>
                    <div className="w-px h-4 bg-gray-300"></div>
                    <span className="text-gray-500">
                      {getDateCount('today')} today
                    </span>
                  </div>
                </div>

                {/* Right side - Actions */}
                <div className="flex items-center space-x-3">
                  {/* Filters */}
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center space-x-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${
                      showFilters 
                        ? 'bg-blue-50 text-blue-600 border border-blue-200' 
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-transparent'
                    }`}
                  >
                    <FaFilter className="w-3.5 h-3.5" />
                    <span>Filter</span>
                    {(dateFilter !== 'all' || locationFilter !== 'all') && (
                      <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-600 text-white rounded-full">
                        {[dateFilter, locationFilter].filter(f => f !== 'all').length}
                      </span>
                    )}
                  </button>

                  {/* Add Daily Update */}
                    <button
                      onClick={() => setShowAddDailyUpdate(true)}
                      className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm"
                    >
                      <FaPlus className="w-3.5 h-3.5" />
                      <span>Add Daily Update</span>
                    </button>
                </div>
              </div>

              {/* Filter Panel */}
              {showFilters && (
                <div className="mt-4 pt-4" style={{ borderTop: '0.5px solid #E5E7EB' }}>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div style={getFormSelectContainerStyle()}>
                      <select
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.target.value)}
                        style={getFormSelectStyle()}
                      >
                        <option value="all">All dates</option>
                        <option value="today">Today ({getDateCount('today')})</option>
                        <option value="yesterday">Yesterday ({getDateCount('yesterday')})</option>
                        <option value="this_week">This week ({getDateCount('this_week')})</option>
                      </select>
                    </div>
                    <div style={getFormSelectContainerStyle()}>
                      <select
                        value={locationFilter}
                        onChange={(e) => setLocationFilter(e.target.value)}
                        style={getFormSelectStyle()}
                      >
                        <option value="all">All locations</option>
                        {uniqueLocations.map((location) => (
                          <option key={location} value={location}>
                            {location}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {/* Active Filters & Clear */}
                  {(dateFilter !== 'all' || locationFilter !== 'all') && (
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-500">Active filters:</span>
                        {dateFilter !== 'all' && (
                          <span className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-md">
                            Date: {dateFilter.replace('_', ' ')}
                            <button onClick={() => setDateFilter('all')} className="ml-1 text-blue-500 hover:text-blue-700">×</button>
                          </span>
                        )}
                        {locationFilter !== 'all' && (
                          <span className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-md">
                            Location: {locationFilter}
                            <button onClick={() => setLocationFilter('all')} className="ml-1 text-blue-500 hover:text-blue-700">×</button>
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          setDateFilter('all');
                          setLocationFilter('all');
                        }}
                        className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                      >
                        Clear all
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Table Header */}
            <div className="px-6 py-2">
              <div className="flex items-center text-xs font-medium" style={getTableHeaderStyle()}>
                <div className="w-8"></div> {/* Expand button space */}
                <div className="w-4 mr-4"></div> {/* Status indicator space */}
                <div className="mx-3 w-24" role="columnheader">Date</div>
                <div className="mx-3 w-24" role="columnheader">Location</div>
                <div className="mx-3 w-16" role="columnheader"># of Resource Onsite</div>
                <div className="flex-[2] mx-3" role="columnheader">What was Done?</div>
                <div className="flex-[1.5] mx-3" role="columnheader">What is the goal for next day?</div>
                <div className="flex-[1.5] mx-3" role="columnheader">What are the road blocks?</div>
                <div className="ml-3 w-32" role="columnheader">Docs</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Updates List - Scrollable Content */}
      <div 
        className="max-w-[1280px] mx-auto px-0 -mt-4"
        style={{ position: 'relative', zIndex: 50 }}
      >
        <div className="bg-white">
          {/* Daily Update Rows */}
          <div>
            {filteredDailyUpdates.sort((a: ProjectDailyUpdate, b: ProjectDailyUpdate) => {
              // Sort by date descending (newest first)
              return new Date(b.date).getTime() - new Date(a.date).getTime();
            }).map((dailyUpdate) => (
              <DailyUpdateRow 
                key={dailyUpdate.id} 
                dailyUpdate={dailyUpdate} 
                projectData={projectData}
                onSubmit={updateDailyUpdateHandler}
                onDelete={deleteDailyUpdateHandler}
                onProjectUpdate={onProjectUpdate}
                isHighlighted={highlightedItemId === dailyUpdate.id}
              />
            ))}
          </div>

          {/* Empty State */}
          {filteredDailyUpdates.length === 0 && (
            <div className="text-center py-16">
              <div className="text-gray-400 mb-4">
                <FaCalendarAlt className="w-12 h-12 mx-auto" />
              </div>
              <h3 className="text-lg font-light text-gray-900 mb-2">No daily updates found</h3>
              <p className="text-gray-600 font-light mb-8">
                {searchQuery || dateFilter !== 'all' || locationFilter !== 'all'
                  ? 'Try adjusting your search or filters.'
                  : 'Get started by adding your first daily update.'
                }
              </p>
                <button
                  onClick={() => setShowAddDailyUpdate(true)}
                  className="bg-white border border-gray-200 text-gray-600 px-6 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-colors"
                >
                  Add First Daily Update
                </button>
            </div>
          )}
        </div>
      </div>

      {/* Add Daily Update Modal */}
      {showAddDailyUpdate && (
        <AddDailyUpdateModal
          isOpen={showAddDailyUpdate}
          onClose={() => setShowAddDailyUpdate(false)}
          onAddItem={handleAddDailyUpdate}
          projectData={projectData}
        />
      )}
    </div>
  );
}; 